import type { ConversationSummaryT, DemandListItem } from '../data/api.js';

export type ShellSearchToken = {
  kind: 'state' | 'site' | 'project' | 'questions' | 'mine';
  value: string;
  raw: string;
  label: string;
  offset?: number;
};

const states: Record<string, string> = {
  draft: 'draft',
  'pending-review': 'submitted',
  'needs-input': 'returned',
  'getting-quotes': 'quoting',
  'ready-to-decide': 'analysed',
  decided: 'decided',
  complete: 'closed',
  rejected: 'rejected',
  cancelled: 'cancelled',
  open: 'open',
  closed: 'closed',
};

const unquote = (value: string): string =>
  value.startsWith('"') && value.endsWith('"') ? value.slice(1, -1).replaceAll('\\"', '"') : value;

export function shellSearchTokenFromRaw(raw: string): ShellSearchToken | undefined {
  const source = raw.trim();
  const state = source.match(/^is:([a-z-]+)$/i);
  if (state?.[1] !== undefined) {
    const name = state[1].toLowerCase();
    // Demands assigned to the signed-in member (FR-5.20, HS2-C63T9N).
    if (name === 'mine') return { kind: 'mine', value: 'mine', raw: 'is:mine', label: 'is:mine' };
    const value = states[name];
    return value === undefined ? undefined : { kind: 'state', value, raw: `is:${name}`, label: `is:${name}` };
  }
  if (/^has:questions$/i.test(source))
    return { kind: 'questions', value: 'questions', raw: 'has:questions', label: 'has:questions' };
  const scoped = source.match(/^(site|project):("(?:\\.|[^"])*"|[^"\s]\S*)$/i);
  if (scoped?.[1] === undefined || scoped[2] === undefined) return undefined;
  const kind = scoped[1].toLowerCase() as 'site' | 'project';
  const value = unquote(scoped[2]).trim();
  if (value === '') return undefined;
  const encoded = /\s/.test(value) ? `"${value.replaceAll('"', '\\"')}"` : value;
  return { kind, value, raw: `${kind}:${encoded}`, label: `${kind}:${value}` };
}

/** Where the caret lands after a commit: a text offset, and whether it follows the chips at that offset. */
export type ShellSearchCaret = { offset: number; afterToken: boolean };

/** The committed text collapses whitespace runs and trims, so offsets come from the same rule applied to a prefix. */
const normalizedLength = (prefix: string, limit: number): number =>
  Math.min(prefix.replace(/\s{2,}/g, ' ').trimStart().length, limit);

/**
 * Turn complete structured expressions into atomic chips; unfinished text stays editable.
 * With a `caret` offset into `input`, also map it into the committed text (HS2-50303H).
 */
export function consumeShellSearchTokens(
  input: string,
  force = false,
  caret?: number,
): { text: string; tokens: ShellSearchToken[]; caret?: ShellSearchCaret } {
  const pattern = /(?:^|\s)((?:is|site|project|has):(?:"(?:\\.|[^"])*"|[^"\s][^\s]*))(?=$|\s)/gi;
  const found: { token: ShellSearchToken; prefix: string; start: number }[] = [];
  let text = '';
  let cursor = 0;
  let caretPrefix: string | undefined;
  for (const match of input.matchAll(pattern)) {
    const raw = match[1];
    if (raw === undefined) continue;
    const start = match.index + match[0].lastIndexOf(raw);
    const end = start + raw.length;
    if (end === input.length && !force && !/\s$/.test(input)) continue;
    const token = shellSearchTokenFromRaw(raw);
    if (token === undefined) continue;
    // A caret before this chip keeps its place in the text; one inside it moves to just after it.
    if (caret !== undefined && caretPrefix === undefined && caret <= end)
      caretPrefix = caret <= start ? text + input.slice(cursor, caret) : (text + input.slice(cursor, start)).trimEnd();
    text += input.slice(cursor, start);
    found.push({ token, prefix: text.trimEnd(), start });
    cursor = end;
  }
  if (caret !== undefined && caretPrefix === undefined)
    caretPrefix = text + input.slice(cursor, Math.max(caret, cursor));
  text += input.slice(cursor);
  const committed = text.replace(/\s{2,}/g, ' ').trim();
  const tokens = found.map(({ token, prefix }) => ({ ...token, offset: normalizedLength(prefix, committed.length) }));
  if (caret === undefined || caretPrefix === undefined) return { text: committed, tokens };
  const offset = normalizedLength(caretPrefix, committed.length);
  const afterToken = found.some(({ start }, index) => start < caret && tokens[index]?.offset === offset);
  return { text: committed, tokens, caret: { offset, afterToken } };
}

/** `viewerMemberId` is the signed-in member, whom `is:mine` matches as the assignee. */
export function filterDemandsForShellSearch(
  rows: DemandListItem[],
  query: string,
  tokens: readonly ShellSearchToken[],
  viewerMemberId?: string | null,
): DemandListItem[] {
  const needle = query.trim().toLowerCase();
  return rows.filter((row) => {
    const textMatches =
      needle === '' ||
      [row.title, row.category ?? '', row.siteName ?? '', row.requesterName ?? ''].some((value) =>
        value.toLowerCase().includes(needle),
      );
    return (
      textMatches &&
      tokens.every((token) => {
        if (token.kind === 'state') return row.status === token.value;
        if (token.kind === 'site') return (row.siteName ?? '').toLowerCase().includes(token.value.toLowerCase());
        if (token.kind === 'questions') return row.openQuestionCount > 0;
        if (token.kind === 'mine') return row.assigneeMemberId !== null && row.assigneeMemberId === viewerMemberId;
        return true;
      })
    );
  });
}

export function filterChatsForShellSearch(
  rows: ConversationSummaryT[],
  query: string,
  tokens: readonly ShellSearchToken[],
): ConversationSummaryT[] {
  const needle = query.trim().toLowerCase();
  return rows.filter((row) => {
    const textMatches =
      needle === '' || [row.title ?? '', row.projectName ?? ''].some((value) => value.toLowerCase().includes(needle));
    return (
      textMatches &&
      tokens.every((token) => {
        if (token.kind === 'state')
          return token.value === 'open'
            ? row.closedAt === null
            : token.value === 'closed'
              ? row.closedAt !== null
              : true;
        if (token.kind === 'project') return (row.projectName ?? '').toLowerCase().includes(token.value.toLowerCase());
        return true;
      })
    );
  });
}

export function mergeShellSearchTokens(
  current: readonly ShellSearchToken[],
  added: readonly ShellSearchToken[],
): ShellSearchToken[] {
  const result = [...current];
  for (const token of added) if (!result.some((item) => item.raw === token.raw)) result.push(token);
  return result;
}

/**
 * Add the filter a search help chip names after the field's typed words (HS2-FMRWC2).
 * A filter already in the search leaves the tokens as they are; an unknown one returns
 * undefined, so the caller leaves the field and popover alone.
 */
export function addShellSearchToken(
  current: readonly ShellSearchToken[],
  raw: string | undefined,
  text: string,
): ShellSearchToken[] | undefined {
  const token = raw === undefined ? undefined : shellSearchTokenFromRaw(raw);
  if (token === undefined) return undefined;
  if (current.some((item) => item.raw === token.raw)) return [...current];
  return mergeShellSearchTokens(current, [{ ...token, offset: text.length }]);
}
